import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPickingRoute,
  isRouteBusy,
  reportShortAtStop,
  scanRouteCode,
  takeAtStop,
  undoLastPick,
} from './operatorPickingRouteThunks';
import {
  MAX_ROUTE_ORDERS,
  ROUTE_PHASE,
  currentStopOf,
  doneStopsOf,
  findItem,
  findStop,
  focusItemOf,
  itemPending,
  routeIdOf,
} from './operatorPickingRouteUtils';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { isTaskOnScreen } from '../operator/operatorTaskScope';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';

/*
 * Picking by route: the orders chosen in the queue to pick together, and the
 * walk through one route — the stop on screen, what was counted there and not
 * sent yet, and what the server is waiting for an answer on.
 */

const initialStop = {
  /** The stop's slot: its identity, which a recalculated route keeps. */
  slotCode: null,
  /** How many stops are done: the one on screen is `index + 1`. */
  index: 0,
  phase: ROUTE_PHASE.SLOT,
  /** Its slot was scanned: only then do its products count. */
  verified: false,
  /** The item counted last: the one «Tomar» goes to. */
  itemKey: null,
};

/** One visit to a route: opening another starts it over, and a trip to the queue does not. */
const initialVisit = {
  routeId: null,
  orderIds: [],
  route: null,
  loading: false,
  error: null,
  requestId: null,
  stop: initialStop,
  /** Units counted of each item, by its `key`, not sent yet. */
  localCounts: {},
  /** Slot codes left for the end, in the order they were skipped. */
  skippedStops: [],
  scan: { lastCode: null },
  /** `'take' | 'short' | 'undo'` while a write is out. */
  pending: null,
  /** `{type, arg, key}` of the write this visit sent last: the only one whose answer it takes. */
  lastWrite: null,
  /** The last take, while it can be undone: the argument «Deshacer» sends. */
  lastPick: null,
  /** `{done, next}` once a stop was finished, until the next one is scanned. */
  hint: null,
};

const initialState = {
  /** Order ids chosen in the queue, in the order they were chosen. */
  selection: [],
  ...initialVisit,
};

const isAwaited = (state, action) => action.meta.requestId === state.lastWrite?.key;

/** Puts the walk on the first stop with something left, keeping where it was if that is still the one. */
const placeStop = (state) => {
  const index = doneStopsOf(state.route);
  const current = currentStopOf(state.route, state.skippedStops);
  if (!current) {
    state.stop = { ...initialStop, index, phase: ROUTE_PHASE.DONE };
    return;
  }
  if (current.slot_code === state.stop.slotCode && state.stop.phase !== ROUTE_PHASE.DONE) {
    state.stop.index = index;
    state.stop.itemKey = focusItemOf(current, state.stop.itemKey)?.key ?? null;
    return;
  }
  state.stop = { ...initialStop, slotCode: current.slot_code, index };
};

/** A route as the server answered it: counts that no longer fit what is left are cut down to it. */
const applyRoute = (state, route) => {
  state.route = route;
  Object.keys(state.localCounts).forEach((key) => {
    const room = itemPending(findItem(route, key));
    if (room > 0 && state.localCounts[key] > 0) state.localCounts[key] = Math.min(state.localCounts[key], room);
    else delete state.localCounts[key];
  });
  placeStop(state);
};

/** What a write sent was counted: once it went through, it is no longer waiting to be sent. */
const discountSent = (state, key, units) => {
  const left = (state.localCounts[key] || 0) - units;
  if (left > 0) state.localCounts[key] = left;
  else delete state.localCounts[key];
};

const startWrite = (pending, type) => (state, action) => {
  // A write of another route — sent again from its band — leaves this visit alone.
  if (action.meta.arg?.routeId !== state.routeId) return;
  state.pending = pending;
  state.lastWrite = { type, arg: intentOf(action.meta.arg), key: action.meta.requestId };
};

const settleWrite = (state, action) => {
  state.pending = null;
  state.lastWrite = null;
  state.hint = action.meta.stopHint ?? null;
};

const rejectWrite = (state, action) => {
  if (action.meta.condition || !isAwaited(state, action)) return;
  state.pending = null;
  if (!isRetryable(action.payload)) state.lastWrite = null;
};

const operatorPickingRouteSlice = createSlice({
  name: 'operatorPickingRoute',
  initialState,
  reducers: {
    /** @param {{payload: {routeId: string, orderIds: number[]}}} action */
    routeEntered: (state, action) => {
      if (state.routeId !== action.payload.routeId) Object.assign(state, initialVisit, action.payload);
    },
    /** «Saltar parada»: the stop on screen goes last. */
    routeStopSkipped: (state) => {
      if (isRouteBusy(state) || !state.stop.slotCode) return;
      const code = state.stop.slotCode;
      state.skippedStops = [...state.skippedStops.filter((other) => other !== code), code];
      state.hint = null;
      placeStop(state);
    },
    /**
     * «+1» on an item of the verified stop: one unit more, never past what it lacks.
     *
     * @param {{payload: string}} action - The item's key.
     */
    routeUnitAdded: (state, action) => {
      if (isRouteBusy(state) || state.stop.phase !== ROUTE_PHASE.ITEM) return;
      const item = (findStop(state.route, state.stop.slotCode)?.items || []).find((one) => one.key === action.payload);
      const counted = state.localCounts[action.payload] || 0;
      if (!item || counted >= itemPending(item)) return;
      state.localCounts[action.payload] = counted + 1;
      state.stop.itemKey = item.key;
    },
    /** @param {{payload: number}} action - An order of the queue, chosen or let go; ten at most. */
    pickingSelectionToggled: (state, action) => {
      const id = action.payload;
      if (state.selection.includes(id)) state.selection = state.selection.filter((other) => other !== id);
      else if (state.selection.length < MAX_ROUTE_ORDERS) state.selection.push(id);
    },
    pickingSelectionCleared: (state) => {
      state.selection = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorWarehouseSelected, () => initialState)

      .addCase(fetchPickingRoute.pending, (state, action) => {
        if (routeIdOf(action.meta.arg.orderIds) !== state.routeId) return;
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchPickingRoute.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        applyRoute(state, action.payload);
      })
      .addCase(fetchPickingRoute.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanRouteCode.fulfilled, (state, action) => {
        const read = action.payload;
        if (read.routeId !== state.routeId) return;
        state.scan.lastCode = acceptedCode(action);
        if (read.kind === 'slot') {
          state.stop.verified = true;
          state.stop.phase = ROUTE_PHASE.ITEM;
          state.hint = null;
          return;
        }
        state.localCounts[read.key] = (state.localCounts[read.key] || 0) + read.units;
        state.stop.itemKey = read.key;
      })

      .addCase(takeAtStop.pending, startWrite('take', takeAtStop.typePrefix))
      .addCase(takeAtStop.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        const { arg } = action.meta;
        settleWrite(state, action);
        discountSent(state, arg.itemKey, Number(arg.quantity));
        state.lastPick = action.payload.pick_key
          ? {
              routeId: arg.routeId,
              routeName: arg.routeName,
              pickKey: action.payload.pick_key,
              slotCode: arg.slotCode,
              sku: arg.sku,
              units: Number(arg.quantity),
            }
          : null;
        if (action.payload.route) applyRoute(state, action.payload.route);
      })
      .addCase(takeAtStop.rejected, rejectWrite)

      .addCase(reportShortAtStop.pending, startWrite('short', reportShortAtStop.typePrefix))
      .addCase(reportShortAtStop.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state, action);
        delete state.localCounts[action.meta.arg.itemKey];
        state.lastPick = null;
        if (action.payload.route) applyRoute(state, action.payload.route);
      })
      .addCase(reportShortAtStop.rejected, rejectWrite)

      .addCase(undoLastPick.pending, startWrite('undo', undoLastPick.typePrefix))
      .addCase(undoLastPick.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state, action);
        state.lastPick = null;
        if (action.payload.route) applyRoute(state, action.payload.route);
      })
      .addCase(undoLastPick.rejected, rejectWrite);
  },
});

export const {
  routeEntered,
  routeStopSkipped,
  routeUnitAdded,
  pickingSelectionToggled,
  pickingSelectionCleared,
} = operatorPickingRouteSlice.actions;

/** The stop on screen, as the route has it now. */
export const selectRouteStop = (state) => {
  const visit = state.operatorPickingRoute;
  return findStop(visit.route, visit.stop.slotCode);
};

/** A write whose answer was lost: nothing else goes, and no read is taken, until it is sent again. */
export const selectRouteUnconfirmed = (state) => {
  const visit = state.operatorPickingRoute;
  return Boolean(visit.lastWrite && !visit.pending);
};

/** Whether a read code is welcome right now. */
export const selectRouteAccepting = (state) => !isRouteBusy(state.operatorPickingRoute);

/**
 * Whether «Deshacer» of a band can go: the take it names is the last of the
 * route on screen, and nothing waits to be sent again.
 *
 * @param {object} state
 * @param {{routeId: string, pickKey: string}} take
 */
export const selectRouteUndoable = (state, { routeId, pickKey }) => {
  const visit = state.operatorPickingRoute;
  return (
    isTaskOnScreen(state, OPERATOR_FLOW.PICKING, routeId) &&
    visit.routeId === routeId &&
    visit.lastPick?.pickKey === pickKey &&
    !(visit.lastWrite && !visit.pending)
  );
};

export default operatorPickingRouteSlice.reducer;
